import React, { useState } from "react";
import { Box } from "@mui/material";
import {
  Addon,
  CreateAddOnsInput,
  EditAddOnsInput,
} from "../../../../types/graphQlQuery.interface";
import { AppCSS } from "../../../../components";
import { CustomerProjectDetails } from "../../../../redux/types/projectDetails";
import { useMutation } from "@apollo/client";
import {
  CREATE_PROJECT_ADDON,
  DELETE_PROJECT_ADDON,
  EDIT_PROJECT_ADDON,
} from "../../../../apolloClient/operation/mutations";
import { toast } from "react-toastify";
import { Prompt } from "../../../../components/dialogue/Dialogue";
import { useFetchProjectDetails } from "../../../../apolloClient/hooks/general/fetchHook";
import { useParams } from "react-router-dom";

interface AddonProps {
  project: CustomerProjectDetails;
  permissions?: boolean;
}

export default function Addons({ project, permissions }: AddonProps) {
  let { id } = useParams();

  const {
    projectDetails: projectDetailsData,
    // loading: projectDetailsLoading,
    // error: projectDetailsError,
    refetch,
  } = useFetchProjectDetails({
    projectId: Number(id),
  });

  const addonsList = projectDetailsData?.insuranceScope?.addOns;

  const [creating, setCreating] = useState(false);
  const [addon, setAddon] = useState<Addon>({
    projectId: project.projectId,
    addonName: "",
    description: "",
    price: null,
    id: 0,
  });

  const [addProjectAddOns] = useMutation<
    {
      addProjectAddons: boolean;
    },
    { input: CreateAddOnsInput }
  >(CREATE_PROJECT_ADDON);

  const [editProjectAddOns] = useMutation<
    {
      editProjectAddOns: boolean;
    },
    { input: EditAddOnsInput }
  >(EDIT_PROJECT_ADDON);

  const saveAddon = () => {
    // Initialize the input object
    const input: any = {
      addonName: addon.addonName,
      description: addon.description,
      price: addon.price,
    };

    // Check if it is a new add-on (id === 0), and set projectId or addonId accordingly
    if (addon.id === 0) {
      input.projectId = addon.projectId;
    } else {
      input.addonId = addon.id;
    }

    // Select the appropriate mutation
    const mutation = addon.id === 0 ? addProjectAddOns : editProjectAddOns;

    // Execute the mutation
    mutation({
      variables: { input },
    })
      .then(() => {
        refetch();
        setCreating(false);
      })
      .catch((error) => {
        toast("Error creating. Please try again.");
        setCreating(false);
      });
  };

  const handleAdd = () => {
    setAddon({
      projectId: project.projectId,
      addonName: "",
      description: "",
      price: null,
      id: 0,
    });
    setCreating(true);
  };

  const handleEdit = (addonToEdit) => {
    setAddon({
      ...addonToEdit,
    });
    setCreating(true);
  };

  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedAddOnId, setSelectedAddOnId] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState("");

  const [deleteAddOn] = useMutation(DELETE_PROJECT_ADDON);

  // Handle the deletion
  const handleDelete = (addOnId) => {
    // Set the selected add-on id and show the confirmation dialog
    setSelectedAddOnId(addOnId);
    setConfirmationMessage("Are you sure you want to delete this add-on?");
    setOpenConfirm(true);
  };

  // When the user confirms the deletion
  const onConfirmDelete = () => {
    deleteAddOn({ variables: { addOnId: selectedAddOnId } })
      .then(() => {
        setOpenConfirm(false);
        refetch();
      })
      .catch((error) => {
        setOpenConfirm(false);
      });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box
          sx={{
            fontSize: { xs: 18, md: 20 },
            fontWeight: 600,
            color: "#1f2937",
          }}
        >
          Add-Ons
        </Box>
        <Box sx={{ display: "flex", gap: 1, width: { xs: "100%", sm: "auto" } }}>
          {permissions && !creating && (
            <Box
              component="button"
              onClick={handleAdd}
              sx={{
                px: 3,
                py: 1.5,
                backgroundColor: "#4b87ed",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                transition: "background-color 0.2s",
                width: { xs: "100%", sm: "auto" },
                "&:hover": {
                  backgroundColor: "#3a6fd8",
                },
              }}
            >
              + Add New
            </Box>
          )}
          {creating && (
            <>
              <Box
                component="button"
                onClick={saveAddon}
                sx={{
                  px: 3,
                  py: 1.5,
                  backgroundColor: "#4b87ed",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                  flex: { xs: 1, sm: "initial" },
                  "&:hover": {
                    backgroundColor: "#3a6fd8",
                  },
                }}
              >
                {addon.id === 0 ? "Create" : "Save"}
              </Box>
              <Box
                component="button"
                onClick={() => setCreating(false)}
                sx={{
                  px: 3,
                  py: 1.5,
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                  flex: { xs: 1, sm: "initial" },
                  "&:hover": {
                    backgroundColor: "#5a6268",
                  },
                }}
              >
                Cancel
              </Box>
            </>
          )}
        </Box>
      </Box>

      {creating && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 2fr 1fr" },
            gap: 2,
            p: 3,
            mb: 3,
            border: "2px solid #4b87ed",
            borderRadius: "12px",
            backgroundColor: "#f8f9fa",
          }}
        >
          <Box>
            <Box
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: "#374151",
                mb: 1,
              }}
            >
              Line item name
            </Box>
            <input
              value={addon.addonName}
              onChange={(e) =>
                setAddon({ ...addon, addonName: e.target.value })
              }
              style={{
                width: "100%",
                height: 40,
                padding: "0 12px",
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                fontSize: 14,
                outline: "none",
                backgroundColor: "white",
              }}
            />
          </Box>
          <Box>
            <Box
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: "#374151",
                mb: 1,
              }}
            >
              Description
            </Box>
            <input
              value={addon.description}
              onChange={(e) =>
                setAddon({ ...addon, description: e.target.value })
              }
              style={{
                width: "100%",
                height: 40,
                padding: "0 12px",
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                fontSize: 14,
                outline: "none",
                backgroundColor: "white",
              }}
            />
          </Box>
          <Box>
            <Box
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: "#374151",
                mb: 1,
              }}
            >
              Price
            </Box>
            <input
              value={addon.price}
              onChange={(e) =>
                setAddon({ ...addon, price: Number(e.target.value) })
              }
              type="number"
              style={{
                width: "100%",
                height: 40,
                padding: "0 12px",
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                fontSize: 14,
                outline: "none",
                backgroundColor: "white",
              }}
            />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: "2fr 4fr 1.5fr 2fr",
          gap: 2,
          p: 2,
          backgroundColor: "#f8f9fa",
          borderRadius: "8px 8px 0 0",
          fontSize: 13,
          fontWeight: 600,
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        <Box>Name</Box>
        <Box>Description</Box>
        <Box>Price</Box>
        <Box>Actions</Box>
      </Box>

      <Box sx={{ mt: { xs: 2, md: 0 } }}>
        {Array.isArray(addonsList) && addonsList?.length > 0 ? (
          addonsList?.map((addon, index) => (
            <Box
              key={index}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "2fr 4fr 1.5fr 2fr" },
                gap: 2,
                p: 2,
                borderBottom: "1px solid #e5e7eb",
                backgroundColor: index % 2 === 0 ? "#fafafa" : "white",
                "&:hover": {
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              <Box
                sx={{
                  fontSize: 14,
                  color: "#1f2937",
                  wordBreak: "break-word",
                }}
              >
                <Box
                  sx={{
                    display: { xs: "block", md: "none" },
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#6b7280",
                    mb: 0.5,
                  }}
                >
                  Name
                </Box>
                {addon.addonName}
              </Box>
              <Box
                sx={{
                  fontSize: 14,
                  color: "#6b7280",
                  wordBreak: "break-word",
                }}
              >
                <Box
                  sx={{
                    display: { xs: "block", md: "none" },
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#6b7280",
                    mb: 0.5,
                  }}
                >
                  Description
                </Box>
                {addon.description}
              </Box>
              <Box
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#1f2937",
                }}
              >
                <Box
                  sx={{
                    display: { xs: "block", md: "none" },
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#6b7280",
                    mb: 0.5,
                  }}
                >
                  Price
                </Box>
                ${addon.price}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                {permissions && (
                  <>
                    <Box
                      component="button"
                      onClick={() => handleEdit(addon)}
                      sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: "#4b87ed",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                        "&:hover": {
                          backgroundColor: "#3a6fd8",
                        },
                      }}
                    >
                      Edit
                    </Box>
                    <Box
                      component="button"
                      onClick={() => handleDelete(addon.id)}
                      sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: "#ef4444",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                        "&:hover": {
                          backgroundColor: "#dc2626",
                        },
                      }}
                    >
                      Delete
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          ))
        ) : (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              fontSize: 14,
              color: "#6b7280",
              backgroundColor: "#f9fafb",
              borderRadius: "8px",
            }}
          >
            No add-ons created yet
          </Box>
        )}
      </Box>

      <Prompt
        message={confirmationMessage}
        onConfirm={onConfirmDelete}
        onClose={() => setOpenConfirm(false)}
        open={openConfirm}
      />
    </Box>
  );
}
